// =============================================================================
// AddSideDropdown — preset picker for the apparel "Add Side" action
// -----------------------------------------------------------------------------
// Renders a Button that opens a popover listing every COMMON_SIDES preset.
// Presets whose slug already exists on the UPT are visually disabled and
// labelled "Already added" — same treatment used in the example project's
// Step 3a side editor. Selecting "Custom…" reveals a small inline form so
// merchants can name a side that's not in the preset list.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { Button, Input } from '@tkpod/ui';
import { COMMON_SIDES, type CommonSidePreset } from '@/lib/api/apparel-sides';

export interface AddSideDropdownProps {
  /** Slugs of every PrintArea currently active on the UPT. */
  existingSlugs: Set<string>;
  /** Disable the trigger while the parent has a pending create mutation. */
  disabled?: boolean;
  /** Show a spinner / disable items while a create call is in flight. */
  isCreating?: boolean;
  /** Fired when the merchant picks a preset or submits a custom name. */
  onPick: (payload: { name: string; slug?: string }) => void;
}

export function AddSideDropdown({
  existingSlugs,
  disabled,
  isCreating,
  onPick,
}: AddSideDropdownProps) {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customError, setCustomError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close popover on outside click or Escape — same UX as the example
  // project's preset menus and consistent with other admin popovers.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setShowCustom(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setShowCustom(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setShowCustom(false);
      setCustomName('');
      setCustomError(null);
    }
  }, [open]);

  const handlePreset = (preset: CommonSidePreset) => {
    if (existingSlugs.has(preset.slug) || isCreating) return;
    onPick({ name: preset.name, slug: preset.slug });
    setOpen(false);
  };

  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = customName.trim();
    if (trimmed.length === 0) {
      setCustomError('Side name is required.');
      return;
    }
    if (trimmed.length > 80) {
      setCustomError('Side name must be 80 characters or fewer.');
      return;
    }
    onPick({ name: trimmed });
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled || isCreating}
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <svg
          className="mr-1.5 h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
        Add Side
      </Button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-72 rounded-md border border-border bg-surface shadow-lg"
        >
          {!showCustom ? (
            <>
              <ul className="max-h-80 overflow-y-auto py-1">
                {COMMON_SIDES.map((preset) => {
                  const exists = existingSlugs.has(preset.slug);
                  return (
                    <li key={preset.slug}>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={exists || isCreating}
                        onClick={() => handlePreset(preset)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-text-primary hover:bg-surface-secondary disabled:cursor-not-allowed disabled:text-text-tertiary disabled:hover:bg-transparent"
                      >
                        <span>{preset.name}</span>
                        {exists && (
                          <span className="text-xs text-text-tertiary">
                            Already added
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="border-t border-border py-1">
                <button
                  type="button"
                  role="menuitem"
                  disabled={isCreating}
                  onClick={() => setShowCustom(true)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-brand-700 hover:bg-surface-secondary disabled:cursor-not-allowed disabled:text-text-tertiary"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.862 4.487 18.55 2.8a1.875 1.875 0 1 1 2.652 2.652L7.94 18.71l-3.79 1.05 1.05-3.79L16.862 4.487Z"
                    />
                  </svg>
                  Custom side name…
                </button>
              </div>
            </>
          ) : (
            <form
              onSubmit={handleCustomSubmit}
              className="space-y-2 p-3"
              aria-label="Custom side name"
            >
              <label className="block text-sm font-medium text-text-primary">
                Side name
              </label>
              <Input
                autoFocus
                value={customName}
                onChange={(e) => {
                  setCustomName(e.target.value);
                  if (customError) setCustomError(null);
                }}
                placeholder="e.g. Inside Label"
                maxLength={80}
                disabled={isCreating}
              />
              <p className="text-xs text-text-tertiary">
                Slug is auto-generated from the name. Existing names are
                rejected by the server.
              </p>
              {customError && (
                <p className="text-xs text-danger-700">{customError}</p>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCustom(false)}
                  disabled={isCreating}
                >
                  Back
                </Button>
                <Button type="submit" size="sm" disabled={isCreating}>
                  Add side
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
